import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Copy, Printer, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import cfgLogo from "@/assets/cfg-logo.png";

interface ChecklistItem {
  id: string;
  text: string;
  checked: boolean;
}

const STANDARD_ITEMS = [
  "Third party proof of the last 6 months payments (copies of checks, deposits, venmo etc.)",
  "Proof of down payment through Settlement Statement or copy of check",
  "Signed Contract Funding Group Purchase Agreement",
  "Completed Contract Information form",
  "Completed RESPA compliance form"
];

interface DocumentChecklistProps {
  dealTitle?: string;
  sellerName?: string;
}

const DocumentChecklist: React.FC<DocumentChecklistProps> = ({ dealTitle, sellerName }) => {
  const { toast } = useToast();
  const [showPreview, setShowPreview] = useState(false);
  const [showEnvelopeInstructions, setShowEnvelopeInstructions] = useState(false);
  const [customItemText, setCustomItemText] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  
  const [items, setItems] = useState<ChecklistItem[]>(
    STANDARD_ITEMS.map((text, index) => ({
      id: `standard-${index}`,
      text,
      checked: true
    }))
  );

  const addCustomItem = () => {
    if (customItemText.trim()) {
      const newItem: ChecklistItem = {
        id: `custom-${Date.now()}`,
        text: customItemText.trim(),
        checked: true
      };
      setItems([...items, newItem]);
      setCustomItemText('');
    }
  };

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const toggleItem = (id: string) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  const getChecklistText = () => {
    const selectedItems = items.filter(item => item.checked);
    let text = '';
    
    if (sellerName) {
      text += `Dear ${sellerName},\n\n`;
    } else {
      text += `Hello,\n\n`;
    }
    
    text += `Thank you for choosing Contract Funding Group. To complete the underwriting process, I'm sending you the required paperwork.\n\n`;
    text += `Please review, sign all enclosed documents where indicated, and return them along with the following items:\n\n`;
    
    selectedItems.forEach((item, index) => {
      text += `${index + 1}. ${item.text}\n`;
    });
    
    if (specialInstructions.trim()) {
      text += `\n\nSpecial Instructions:\n${specialInstructions}\n`;
    }
    
    text += `\n\nPlease mail everything back in the enclosed return envelope at your earliest convenience.\n\n`;
    text += `If you have any questions, please don't hesitate to contact me.\n\n`;
    text += `Thank you,\nContract Funding Group`;
    
    return text;
  };

  const handleCopyToClipboard = () => {
    const text = getChecklistText();
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: "Checklist copied and ready to paste into email"
    });
  };

  const handlePrint = () => {
    window.print();
    toast({
      title: "Print Dialog Opened",
      description: "The checklist is ready to print."
    });
  };

  const ReturnEnvelopeInstructions = () => (
    <div className="max-w-2xl mx-auto p-8 bg-background">
      <div className="text-center mb-6">
        <img src={cfgLogo} alt="Contract Funding Group" className="h-28 mx-auto mb-3" />
      </div>
      
      {sellerName && (
        <div className="mb-4">
          <p className="font-semibold text-base">Dear {sellerName},</p>
        </div>
      )}

      <p className="mb-5 text-sm leading-relaxed">
        Thank you for choosing Contract Funding Group. To complete the underwriting process, please follow these steps:
      </p>

      <div className="mb-5">
        <h3 className="font-semibold mb-3 text-base">Instructions:</h3>
        <ol className="space-y-2 list-decimal list-inside text-sm">
          <li className="pl-2 leading-relaxed">Review and sign all enclosed documents where indicated</li>
          <li className="pl-2 leading-relaxed">Gather the following required items:
            <ul className="ml-8 mt-2 space-y-1.5 list-disc list-inside">
              {items.filter(item => item.checked).map((item) => (
                <li key={item.id} className="text-sm">{item.text}</li>
              ))}
            </ul>
          </li>
          <li className="pl-2 leading-relaxed">Place all signed documents and required items in the enclosed return envelope</li>
          <li className="pl-2 leading-relaxed">Mail the envelope back to us at your earliest convenience</li>
        </ol>
      </div>

      {specialInstructions.trim() && (
        <div className="mb-5 p-4 bg-muted rounded">
          <h3 className="font-semibold mb-2 text-sm">Special Instructions:</h3>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{specialInstructions}</p>
        </div>
      )}

      <div className="mt-6 pt-4 border-t">
        <p className="mb-3 text-sm">
          If you have questions, please contact us.
        </p>
        <p className="font-semibold text-base">Thank you,</p>
        <p className="text-muted-foreground text-sm">Contract Funding Group</p>
      </div>
    </div>
  );

  const ChecklistPreview = () => (
    <div className="max-w-2xl mx-auto p-8 bg-background">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold">Contract Funding Group</h1>
        <p className="text-muted-foreground">Paperwork Checklist</p>
      </div>
      
      {(dealTitle || sellerName) && (
        <div className="mb-6 p-4 bg-muted rounded-lg">
          {dealTitle && <p className="font-semibold">Deal: {dealTitle}</p>}
          {sellerName && <p>Seller: {sellerName}</p>}
        </div>
      )}

      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">Required Documentation:</h3>
        <ul className="space-y-3">
          {items.filter(item => item.checked).map((item, index) => (
            <li key={item.id} className="flex items-start gap-3">
              <span className="font-semibold min-w-[30px]">{index + 1}.</span>
              <span>{item.text}</span>
            </li>
          ))}
        </ul>
      </div>

      {specialInstructions.trim() && (
        <div className="mb-8 p-4 bg-muted rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Special Instructions:</h3>
          <p className="whitespace-pre-wrap">{specialInstructions}</p>
        </div>
      )}

      <div className="mt-8 pt-6 border-t">
        <p className="mb-4">
          Please gather these documents for the underwriting process. If you have any questions, 
          please don't hesitate to contact us.
        </p>
        <p className="font-semibold">Thank you,</p>
        <p className="text-muted-foreground">Contract Funding Group</p>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 0.5in;
            margin: 0;
          }
          @page {
            margin: 0.5in;
          }
        }
      `}</style>
      
      <div className="space-y-6 print:hidden">
        <Card>
          <CardHeader>
            <CardTitle>Paperwork Checklist</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {items.map((item) => (
                <div key={item.id} className="flex items-start gap-3 p-3 border rounded-lg">
                  <Checkbox
                    checked={item.checked}
                    onCheckedChange={() => toggleItem(item.id)}
                    className="mt-1"
                  />
                  <span className="flex-1 text-sm">{item.text}</span>
                  {item.id.startsWith('custom-') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem(item.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="pt-4 border-t">
              <Label htmlFor="specialInstructions" className="mb-2 block">Special Instructions</Label>
              <Textarea
                id="specialInstructions"
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                placeholder="Add any special instructions for the seller..."
                rows={3}
                className="mb-4"
              />
            </div>

            <div className="pt-4 border-t">
              <Label htmlFor="customItem" className="mb-2 block">Add Custom Item</Label>
              <div className="flex gap-2">
                <Input
                  id="customItem"
                  value={customItemText}
                  onChange={(e) => setCustomItemText(e.target.value)}
                  placeholder="Enter custom checklist item"
                  onKeyPress={(e) => e.key === 'Enter' && addCustomItem()}
                />
                <Button onClick={addCustomItem} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 flex-wrap">
              <Button onClick={() => setShowPreview(true)} variant="outline">
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              
              <Button onClick={() => setShowEnvelopeInstructions(true)} variant="outline">
                <Printer className="h-4 w-4 mr-2" />
                Return Envelope Instructions
              </Button>
              
              <Button onClick={handleCopyToClipboard} variant="outline">
                <Copy className="h-4 w-4 mr-2" />
                Copy to Clipboard
              </Button>
              
              <Button onClick={handlePrint} variant="outline">
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </CardContent>
        </Card>

        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Checklist Preview</DialogTitle>
            </DialogHeader>
            <ChecklistPreview />
          </DialogContent>
        </Dialog>

        <Dialog open={showEnvelopeInstructions} onOpenChange={setShowEnvelopeInstructions}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Return Envelope Instructions</DialogTitle>
            </DialogHeader>
            <ReturnEnvelopeInstructions />
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={() => {
                window.print();
                toast({
                  title: "Print Dialog Opened",
                  description: "Instructions are ready to print."
                });
              }}>
                <Printer className="h-4 w-4 mr-2" />
                Print Instructions
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Hidden print version */}
      <div className="hidden print:block print-content">
        {showEnvelopeInstructions ? <ReturnEnvelopeInstructions /> : <ChecklistPreview />}
      </div>
    </>
  );
};

export default DocumentChecklist;
